import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth.store';
import { usePermissionStore } from '../stores/permission.store';
import { login as loginApi, logout as logoutApi, getAuthMe, authKeys } from '../api/auth.api';
import { tokenManager, resetAuthInterceptorState } from '../api/client';
import { toast } from '../lib/notification';
import { getAuthClient, getTenantIdentifier } from '../bootstrap/subdomain';
import type { LoginRequest } from '../types/auth.types';

/**
 * Authentication hook
 * Provides auth state and actions
 */
export function useAuth() {
	const queryClient = useQueryClient();
	const authStore = useAuthStore();
	const permissionStore = usePermissionStore();

	// Initialize auth state on mount
	useEffect(() => {
		authStore.initialize();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Check if we have tokens
	const hasTokens = tokenManager.hasTokens();

	// Fetch user info and permissions when tokens exist
	// This verifies tokens on page load/refresh
	// Skip when enforcePasswordChange is true — the server returns 403 on all protected APIs
	const { data, isLoading, error, refetch } = useQuery({
		queryKey: authKeys.me(),
		queryFn: getAuthMe,
		enabled: hasTokens && !authStore.enforcePasswordChange,
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes (renamed from cacheTime in v5)
		retry: false,
		refetchOnMount: true, // Always refetch on mount to avoid stale data
		refetchOnReconnect: true,
		refetchOnWindowFocus: false,
	});

	// Update stores when data changes (successful token verification).
	// Only overwrite permissions when the response actually contains a
	// non-empty array — an empty array from a degraded /me response
	// (e.g. after token refresh with Redis cache miss) must not wipe
	// a known-good permission set.
	useEffect(() => {
		if (data) {
			const user = data.user ?? data;
			const permissions = data.permissions;
			authStore.setUser(user);
			if (Array.isArray(permissions) && permissions.length > 0) {
				permissionStore.setPermissions(permissions);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	// Login mutation
	const loginMutation = useMutation({
		mutationFn: (credentials: LoginRequest) =>
			loginApi({ ...credentials, client: getAuthClient(), subDomain: getTenantIdentifier() ?? '' }),
		onSuccess: (user) => {
			console.log('[useAuth] Login success, user:', user);
			resetAuthInterceptorState();
			tokenManager.setTokens();

			authStore.login(user, user.enforcePasswordChange);

			if (user.permissions) {
				permissionStore.setPermissions(user.permissions);
			}

			// Fetch full profile + permissions from /me
			if (!user.enforcePasswordChange) {
				refetch().catch((err) => {
					console.error('[useAuth] auth/me refetch failed:', err);
				});
			}

			toast.success('Login successful');
		},
		onError: () => {
			// Error is surfaced via loginMutation.error in the LoginForm as an inline alert
		},
	});

	// Logout mutation
	const logoutMutation = useMutation({
		mutationFn: logoutApi,
		onSuccess: () => {
			tokenManager.clearTokens();
			authStore.logout();
			permissionStore.reset();
			queryClient.clear();
			toast.success('Logged out successfully');
		},
		onError: (error: Error) => {
			// Still clear local state even if API call fails
			tokenManager.clearTokens();
			authStore.logout();
			permissionStore.reset();
			queryClient.clear();
			console.error('Logout error:', error);
		},
	});

	return {
		// State
		user: authStore.user,
		isAuthenticated: authStore.isAuthenticated,
		isLoading: isLoading || authStore.isLoading,
		isInitialized: authStore.isInitialized,
		enforcePasswordChange: authStore.enforcePasswordChange,
		error,

		// Actions
		login: loginMutation.mutateAsync,
		logout: logoutMutation.mutateAsync,
		refetch,

		// Loading states
		isLoggingIn: loginMutation.isPending,
		isLoggingOut: logoutMutation.isPending,

		// Login error for inline display
		loginError: loginMutation.error,
		resetLoginError: loginMutation.reset,
	};
}
